/* eslint-disable */
import * as Sentry from '@sentry/browser'

const API = process.env.VUE_APP_CIPHER_API;

const SESSION_PARAMS = (caId, isSigned) => ({
  caId,
  cadesType: "CAdESXLong",
  signatureType: "attached",
  ...isSigned
    ? {
      dataToSignQualifier: "alreadySigned",
      duplicateSign: "true"
    }
    : {}
})

const CHECK_TIMEOUT = 500;

const callApi = async (endpoint, data, method = 'GET', headers = {}, noParse = false) => {
  const isFormData = data instanceof FormData;
  const isFile = data instanceof Blob;

  const request = {
    headers: {
      Accept: 'application/json',
      ...(isFormData || isFile
        ? {}
        : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    method,
  };

  if (data && (isFormData || isFile)) {
    Object.assign(request, { body: data });
  } else if (data) {
    Object.assign(request, { body: JSON.stringify(data) });
  }

  const responsePromise = await fetch(endpoint, request);
  const responseClone = responsePromise.clone();

  if (responsePromise.status >= 400) {
    const errorData = await responsePromise.json();
    throw new Error(errorData.failureCause || errorData.message)
  } else {
    if (noParse) {
      return responsePromise;
    }
    return await responsePromise.json();
  }
}

export const getCertificateAuthorities = async () => {
  const response = await callApi(`${API}/certificateAuthority/supported`);

  return (response.ca || []).map(({ name, id }) => ({ name, optionId: id }));
}


const sessionStart = async () => {
  const response = await callApi(`${API}/ticket`, null, 'POST');

  return response.ticketUuid;
}

const setSessionParams = async (ticketUuid, params) => {
  const response = await callApi(`${API}/ticket/${ticketUuid}/option`, params, 'PUT');

  return response.settedOptions;
}

const setSessionData = async (ticketUuid, file) => {
  const response = await callApi(
    `${API}/ticket/${ticketUuid}/data`,
    file,
    'POST',
    { 'Content-Type': 'application/octet-stream' }
  );

  return response.message;
}

const setSignedSessionData = async (ticketUuid, file) => {
const response = await callApi(
    `${API}/ticket/${ticketUuid}/ds/data`,
    file,
    'POST',
    { 'Content-Type': 'application/octet-stream' }
  );

  return response.message;
}

const setSessionContainer = async (ticketUuid, file) => {
  const response = await callApi(
    `${API}/ticket/${ticketUuid}/keyStore`,
    file,
    'PUT',
    { 'Content-Type': 'application/octet-stream' }
  );

  return response.message;
}

const signSessionData = async (ticketUuid, keyStorePassword) => {
  const response = await callApi(
    `${API}/ticket/${ticketUuid}/ds/creator`,
    { keyStorePassword },
    'POST'
  );

  return response.message;
}


const checkSessionSignatureStatus = async(ticketUuid) => {
  const response = await callApi(`${API}/ticket/${ticketUuid}/ds/creator`, null, 'GET', {}, true);

  if (response.status !== 200 && response.status !== 202) {
    const data = await response.json();
    throw new Error(data.message);
  }

  return response.status === 200;
}

const getSignedData = async (ticketUuid) => {
  const response = await callApi(`${API}/ticket/${ticketUuid}/ds/data`, null, 'GET', { 'Accept': 'application/octet-stream' }, true );
  const blob = await response.blob();

  return blob;
}

const deleteSession = async (ticketUuid) =>  {
  const response = await callApi(`${API}/ticket/${ticketUuid}`, null, 'DELETE');

  return response.ticketUuid;
}

const wait = async (ms) => new Promise(resolve => {
  setTimeout(resolve, ms);
});

export const signDocumentFlow = async ({
  file,
  document,

  caId,
  container,
  password,
}) => {
  const {
    p7s,
    signatures,
    name: fileName,
    mime_type,
    client_mime_type
  } = document;

  const isSigned = (signatures && signatures.length > 0) || (p7s && p7s.length > 0);
  const isPdf = mime_type.includes('pdf') || client_mime_type.includes('pdf');
  const isP7s = mime_type.includes('pkcs7-signature') || client_mime_type.includes('pkcs7-signature');

  const newFileName = isPdf || isP7s
    ? fileName
    : `${fileName}.p7s`;

  // Создать сессию
  const ticketUuid = await sessionStart();

  let result;
  let error;
  let step;
  try {
    step = 'Загрузить данные сессии или Загрузить данные ЭП';
    if (isSigned) {
      await setSignedSessionData(ticketUuid, file);
    } else {
      await setSessionData(ticketUuid, file);
    }

    step = 'Установить параметры сессии';
    await setSessionParams(ticketUuid, SESSION_PARAMS(caId, isSigned));

    step = 'Загрузить ключевой контейнер сессии';
    await setSessionContainer(ticketUuid, container);

    step = 'Создать ЭП';
    await signSessionData(ticketUuid, password);

    step = 'Получить результат создания ЭП';
    let inProgress = false;
    while (!inProgress) {
      inProgress = await checkSessionSignatureStatus(ticketUuid);
      await wait(CHECK_TIMEOUT);
    }

    step = 'Получить данные ЭП';
    const signedBlob = await getSignedData(ticketUuid);

    step = 'format';
    result = new File([signedBlob], newFileName);
  } catch (e) {
    Sentry.configureScope(scope => scope.setExtras({ step }))
    Sentry.captureException(e)
    Sentry.configureScope(scope => scope.clear())

    error = e.message;
  } finally {
    // Удалить сессию
    await deleteSession(ticketUuid);
  }

  return {
    error,
    result
  };
}
